import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import axiosInstance from '../api';
import { environment } from '../../environments/environment';
import AddIcon from '@mui/icons-material/Add';
import GroupsIcon from '@mui/icons-material/Groups';
import HubIcon from '@mui/icons-material/Hub';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HelpIcon from '@mui/icons-material/Help';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SortIcon from '@mui/icons-material/Sort';
import AnalyticsIcon from '@mui/icons-material/Analytics';

import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import CloseIcon from '@mui/icons-material/Close';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { Menu, MenuItem } from '@mui/material';
import EntityDetailDrawer from './EntityDetailDrawer';
import MarkdownCardPreview from './MarkdownCardPreview';
import { clearStoredAuthTokens } from '../utils/auth';
import { Project, invalidateProjectList, listProjects, setStoredProjectId, clearProjectCache } from '../services/projectService';

import Sidebar from './Sidebar';
import { dsVar } from '../theme/tokens';

interface Operator {
    _id: string;
    id: string;
    operator_name: string;
    operator_description: string;
    flow_template_code?: string;
    functionalDomains?: { code: string; name: string }[];
    industrySectors?: { code: string; name: string }[];
}

const formatTimeAgo = (dateString?: string) => {
    if (!dateString) return 'recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    if (diffInHours < 1) return 'just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
};

const IntelligenceHub = () => {
    const navigate = useNavigate();
    const [operators, setOperators] = useState<Operator[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [projectsLoading, setProjectsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [detailOperator, setDetailOperator] = useState<Operator | null>(null);

    // New project modal
    const [newProjectModalOpen, setNewProjectModalOpen] = useState(false);
    const [newProjectName, setNewProjectName] = useState("");
    const [newProjectSubmitting, setNewProjectSubmitting] = useState(false);
    const [newProjectError, setNewProjectError] = useState<string | null>(null);

    const handleUserMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setUserMenuAnchorEl(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchorEl(null);
    };

    const handleSignOut = () => {
        clearStoredAuthTokens();
        clearProjectCache();
        handleUserMenuClose();
        navigate('/login');
    };

    const openNewProjectModal = () => {
        setNewProjectName("");
        setNewProjectError(null);
        setNewProjectModalOpen(true);
    };

    const closeNewProjectModal = () => {
        if (newProjectSubmitting) return;
        setNewProjectModalOpen(false);
        setNewProjectError(null);
    };

    const handleCreateProject = async () => {
        const trimmedName = newProjectName.trim();
        if (!trimmedName) {
            setNewProjectError("Project name is required.");
            return;
        }
        try {
            setNewProjectSubmitting(true);
            setNewProjectError(null);
            const orgId = localStorage.getItem('org_id');
            const response = await axiosInstance.post('/projects', {
                projectName: trimmedName,
                orgId,
            });
            const created: Project = response.data?.project || response.data;
            const projectId = created?._id;
            invalidateProjectList();
            if (projectId) setStoredProjectId(projectId);
            setNewProjectModalOpen(false);
            navigate(`/analytics/projects/${projectId}`);
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to create project. Please try again.';
            setNewProjectError(msg);
        } finally {
            setNewProjectSubmitting(false);
        }
    };

    useEffect(() => {
        const fetchTopOperators = async () => {
            try {
                const orgId = localStorage.getItem('org_id');
                if (!orgId) {
                    console.error("No org_id found in local storage");
                    setLoading(false);
                    return;
                }

                const response = await axiosInstance.get(
                    `/operators?orgId=${orgId}&limit=2`
                );

                if (response.data && response.data.operators) {
                    setOperators(response.data.operators);
                }
            } catch (error) {
                console.error("Failed to fetch operators:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchTopOperators();
    }, []);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                setProjectsLoading(true);
                const orgId = localStorage.getItem('org_id');
                if (!orgId) {
                    setProjectsLoading(false);
                    return;
                }

                const query = searchQuery.trim();
                if (query) {
                    const response = await axiosInstance.get(
                        `/projects/search?organisationId=${orgId}&query=${encodeURIComponent(query)}`
                    );
                    setProjects(response.data?.projects ?? []);
                } else {
                    // force: this grid is the authoritative list, so it refetches
                    // rather than reusing whatever the workspace selector cached.
                    setProjects(await listProjects({ force: true }));
                }
            } catch (error) {
                console.error("Failed to fetch projects:", error);
            } finally {
                setProjectsLoading(false);
            }
        };

        const timerId = setTimeout(() => {
            fetchProjects();
        }, 300);

        return () => clearTimeout(timerId);
    }, [searchQuery]);

    return (
        <div className="flex h-screen overflow-hidden bg-page font-sans text-fg selection:bg-accent selection:text-on-accent">
            <Sidebar activePage="projects" />

            {/* Main Content */}
            <main className="flex-1 flex flex-col overflow-hidden bg-page">
                {/* Global Header Search */}
                <header className="h-16 border-b border-line/50 bg-page/80 backdrop-blur-md px-8 flex items-center justify-between sticky top-0 z-10 m-0">
                    <div className="flex-1 max-w-2xl text-left">
                        <div className="relative group flex items-center justify-start h-full">
                            <SearchIcon className="absolute left-3 text-fg-muted group-focus-within:text-accent-fg transition-colors" />
                            <input
                                className="w-full bg-surface border-none rounded-lg py-2 pl-10 pr-4 text-sm focus:ring-1 focus:ring-accent-fg placeholder:text-fg-muted text-fg outline-none"
                                placeholder="Search content, operators, or previous analysis..."
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-4 ml-8">
                        <button className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center">
                            <NotificationsIcon />
                        </button>
                        <button className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center">
                            <HelpIcon />
                        </button>
                        <button
                            className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
                            onClick={handleUserMenuOpen}
                            aria-label="Open user menu"
                        >
                            <AccountCircleIcon />
                        </button>
                        <Menu
                            anchorEl={userMenuAnchorEl}
                            open={Boolean(userMenuAnchorEl)}
                            onClose={handleUserMenuClose}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            PaperProps={{
                                sx: {
                                    borderRadius: '12px',
                                    backgroundColor: dsVar('surface'),
                                    color: dsVar('fg-body'),
                                    border: `1px solid ${dsVar('line')}`,
                                    minWidth: '180px',
                                    mt: 1
                                }
                            }}
                        >
                            <MenuItem
                                disabled
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: dsVar('fg-2'),
                                    borderBottom: `1px solid ${dsVar('line')}`,
                                    opacity: '1 !important'
                                }}
                            >
                                {localStorage.getItem('org_id') || 'User'}
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    handleUserMenuClose();
                                    navigate('/access-control');
                                }}
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    fontSize: '0.9rem',
                                    color: dsVar('fg-body'),
                                    '&:hover': {
                                        backgroundColor: dsVar('fg', 0.08)
                                    }
                                }}
                            >
                                <AccountCircleIcon fontSize="small" />
                                Your account
                            </MenuItem>
                            <MenuItem
                                onClick={handleSignOut}
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    fontSize: '0.9rem',
                                    color: dsVar('accent-fg'),
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 222, 89, 0.08)'
                                    }
                                }}
                            >
                                <LogoutIcon fontSize="small" />
                                Sign out
                            </MenuItem>
                        </Menu>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto p-8 space-y-12 text-left">
                    <div className="mb-10 flex flex-col gap-4 m-0 text-left items-start">
                        <h1 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-fg m-0 mr-auto p-0 font-mono">
                            Your <span className="text-fg font-normal m-0">workspace</span>
                        </h1>
                        <p className="text-fg-2 text-lg max-w-2xl font-light leading-relaxed m-0 p-0 tracking-normal font-sans">
                            Collaborate on data insights and manage your organizational AI projects in one unified workspace.
                        </p>
                    </div>

                    {/* Filters Row */}
                    <div className="flex flex-wrap items-center gap-3 mb-8 m-0 p-0">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-surface border border-line rounded-full text-xs font-medium text-fg-soft hover:border-accent-fg/50 cursor-pointer transition-colors group">
                            <span className="text-fg-muted group-hover:text-accent-fg transition-colors m-0">Project Type:</span>
                            <span className="text-fg-body m-0">All</span>
                            <ExpandMoreIcon fontSize="small" className="m-0" />
                        </div>
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-surface border border-line rounded-full text-xs font-medium text-fg-soft hover:border-accent-fg/50 cursor-pointer transition-colors group">
                            <span className="text-fg-muted group-hover:text-accent-fg transition-colors">Last Modified:</span>
                            <span className="text-fg-body">Any time</span>
                            <ExpandMoreIcon fontSize="small" />
                        </div>
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-surface border border-line rounded-full text-xs font-medium text-fg-soft hover:border-accent-fg/50 cursor-pointer transition-colors group">
                            <span className="text-fg-muted group-hover:text-accent-fg transition-colors">Owner:</span>
                            <span className="text-fg-body">Me</span>
                            <ExpandMoreIcon fontSize="small" />
                        </div>
                        <div className="h-4 w-[1px] bg-line mx-2"></div>
                        <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-fg-2 hover:text-accent-fg cursor-pointer transition-colors">
                            <SortIcon fontSize="small" />
                            <span>Sort by: Recent</span>
                        </div>
                        <button className="ml-auto text-xs text-accent-fg hover:underline font-medium border-none bg-transparent cursor-pointer p-0">
                            Clear all filters
                        </button>
                    </div>

                    <section className="m-0 text-left">
                        <div className="flex items-center justify-between mb-4 m-0 p-0 text-left">
                            <h2 className="font-mono text-xl m-0 p-0 text-left" style={{ color: dsVar('accent-fg') }}>Your projects</h2>
                            <button
                                onClick={openNewProjectModal}
                                className="flex items-center gap-2 px-4 py-2 bg-accent text-on-accent font-bold text-sm rounded-lg hover:brightness-110 transition-all border-none cursor-pointer"
                            >
                                <AddIcon fontSize="small" />
                                New project
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full mt-4">
                            {projectsLoading ? (
                                <p className="text-fg-2">Loading projects...</p>
                            ) : projects.length > 0 ? (
                                projects.map((project, index) => (
                                    <div key={project._id} className="bg-surface border border-line rounded-xl p-4 flex flex-col gap-4 hover:border-accent-fg/30 transition-all group m-0 text-left cursor-pointer" onClick={() => { setStoredProjectId(project._id); navigate(`/analytics/projects/${project._id}`); }}>
                                        <div className="aspect-video rounded-lg bg-page overflow-hidden border border-line/50 group-hover:border-accent-fg/20 transition-all relative flex items-center justify-center">
                                            {/* Subtle dot-grid background */}
                                            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(#ffde59 0.5px, transparent 0.5px)', backgroundSize: '12px 12px' }}></div>
                                            {/* Glowing halo behind icons */}
                                            <div className="absolute w-24 h-24 rounded-full bg-accent/10 blur-2xl"></div>
                                            {/* Goal-based team icon */}
                                            <div className="relative flex flex-col items-center gap-2">
                                                {/* Team / groups icon */}
                                                <div className="w-12 h-12 rounded-full bg-accent/20 border border-accent-fg/30 flex items-center justify-center text-accent-fg shadow-lg" style={{ boxShadow: '0 0 18px rgba(255,222,89,0.25)' }}>
                                                    <GroupsIcon style={{ fontSize: 28 }} />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="m-0 text-left">
                                            <h3 className="font-medium text-lg mb-2 group-hover:text-accent-fg transition-colors m-0 text-fg p-0" style={{ letterSpacing: 'normal' }}>
                                                {project.name || project.projectName}
                                            </h3>
                                            <p className="text-sm text-fg-muted m-0 p-0">Edited {formatTimeAgo(project.lastUpdatedDate)}</p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-fg-2">No projects currently available.</p>
                            )}
                        </div>
                    </section>

                    <section className="mt-12 text-left p-0 m-0">
                        <div className="flex flex-col mb-6 items-start justify-start p-0 m-0 w-full mt-10">
                            <h2 className="font-mono text-xl m-0 mb-4 p-0 text-left" style={{ color: dsVar('accent-fg'), width: '100%' }}>Recommended for you</h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                                {loading ? (
                                    <p className="text-fg-2">Loading analysts...</p>
                                ) : operators.length > 0 ? (
                                    operators.map((operator) => (
                                        <div key={operator.id} className="bg-surface border border-line rounded-xl p-6 flex flex-col gap-4 hover:border-accent-fg/50 transition-all duration-300 group m-0 text-left cursor-pointer" style={{ width: '100%' }} onClick={() => setDetailOperator(operator)}>
                                            <div className="flex items-start justify-between m-0">
                                                <div className="w-12 h-12 rounded-lg bg-accent/20 border border-accent-fg/30 flex items-center justify-center text-accent-fg group-hover:scale-110 transition-transform m-0">
                                                    <PsychologyIcon />
                                                </div>
                                                {operator.functionalDomains && operator.functionalDomains.length > 0 && (
                                                    <span className="text-[10px] font-mono text-fg-muted uppercase tracking-tighter m-0 whitespace-nowrap overflow-hidden text-ellipsis max-w-32">{operator.functionalDomains[0].code}</span>
                                                )}
                                            </div>
                                            <div className="m-0 text-left">
                                                <h3 className="font-bold text-lg mb-2 group-hover:text-accent-fg transition-colors m-0 text-fg p-0 font-mono" style={{ letterSpacing: 'normal' }}>{operator.operator_name}</h3>
                                                <MarkdownCardPreview
                                                    text={operator.operator_description}
                                                    className="text-sm text-fg-2 leading-relaxed m-0 p-0 line-clamp-3"
                                                />
                                            </div>
                                            <div className="mt-auto pt-4 m-0 border-t border-fg/5" style={{ width: '100%' }}>
                                                <div className="flex gap-3 m-0" style={{ width: '100%' }}>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/operator-launch/${operator._id || operator.id}`); }}
                                                        className="flex-1 hover:bg-opacity-90 text-on-accent font-bold py-2 px-3 rounded-lg flex items-center justify-center gap-1 transition-colors text-sm border-none cursor-pointer p-0"
                                                        style={{ backgroundColor: dsVar('accent'), height: '36px' }}
                                                    >
                                                        <RocketLaunchIcon style={{ fontSize: 14 }} />
                                                        <span className="m-0">Add</span>
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); navigate('/AIStudio/customize', { state: { flow_template_code: operator.flow_template_code, operator_id: operator._id || operator.id, operator_name: operator.operator_name, fromOperator: true, returnTo: { pathname: '/intelligence-hub' } } }); }}
                                                        className="flex-1 bg-transparent border border-accent-fg text-accent-fg hover:bg-accent/10 font-bold py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors cursor-pointer p-0"
                                                        style={{ height: '40px' }}
                                                    >
                                                        <SettingsIcon fontSize="small" className="m-0" />
                                                        <span className="m-0">Customize</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-fg-2 m-0">No analysts currently available.</p>
                                )}
                            </div>
                            <div className="flex justify-center mt-8 w-full p-0">
                                <button
                                    onClick={() => navigate('/operators-marketplace')}
                                    className="bg-transparent border border-accent-fg/50 text-accent-fg hover:bg-accent/10 font-bold py-2 px-6 rounded-full transition-colors cursor-pointer flex items-center justify-center gap-2"
                                >
                                    <span className="m-0">View more</span>
                                    <ArrowForwardIcon fontSize="small" className="m-0" />
                                </button>
                            </div>
                        </div>
                    </section>

                </div>
            </main>

            {/* ── New project modal ── */}
            {newProjectModalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center"
                    style={{ backgroundColor: dsVar('page', 0.85), backdropFilter: 'blur(6px)' }}
                    onClick={(e) => { if (e.target === e.currentTarget) closeNewProjectModal(); }}
                >
                    <div
                        className="w-full max-w-md bg-surface border border-line rounded-2xl shadow-2xl p-8 flex flex-col gap-6"
                        style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.6)' }}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between m-0">
                            <h2 className="font-mono text-xl text-fg m-0" style={{ color: dsVar('accent-fg') }}>New project</h2>
                            <button
                                onClick={closeNewProjectModal}
                                disabled={newProjectSubmitting}
                                className="p-1 text-fg-2 hover:text-fg transition-colors bg-transparent border-none cursor-pointer flex items-center justify-center rounded-lg hover:bg-fg/10"
                            >
                                <CloseIcon fontSize="small" />
                            </button>
                        </div>

                        {/* Input */}
                        <div className="flex flex-col gap-2 m-0">
                            <label className="text-xs font-medium text-fg-2 uppercase tracking-widest m-0">Project name</label>
                            <input
                                autoFocus
                                type="text"
                                value={newProjectName}
                                onChange={(e) => { setNewProjectName(e.target.value); setNewProjectError(null); }}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateProject(); if (e.key === 'Escape') closeNewProjectModal(); }}
                                placeholder="e.g. My New Marketing Project"
                                disabled={newProjectSubmitting}
                                className="w-full bg-page border border-line rounded-lg py-3 px-4 text-sm text-fg placeholder:text-fg-faint focus:outline-none focus:ring-1 focus:ring-accent-fg transition-all disabled:opacity-50"
                            />
                            {newProjectError && (
                                <p className="text-red-400 text-xs m-0 mt-1">{newProjectError}</p>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 justify-end m-0">
                            <button
                                onClick={closeNewProjectModal}
                                disabled={newProjectSubmitting}
                                className="px-5 py-2.5 rounded-lg text-sm font-medium text-fg-soft bg-transparent border border-line hover:border-fg-2 transition-colors cursor-pointer disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateProject}
                                disabled={newProjectSubmitting || !newProjectName.trim()}
                                className="px-5 py-2.5 rounded-lg text-sm font-bold text-on-accent bg-accent hover:brightness-110 transition-all border-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {newProjectSubmitting ? (
                                    <>
                                        <span className="inline-block w-3.5 h-3.5 border-2 border-page/40 border-t-page rounded-full animate-spin"></span>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <AddIcon fontSize="small" />
                                        Create project
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Analyst Detail Drawer */}
            <EntityDetailDrawer
                open={Boolean(detailOperator)}
                onClose={() => setDetailOperator(null)}
                title={detailOperator?.operator_name ?? ''}
                subtitle={detailOperator?.functionalDomains?.[0]?.code}
                description={detailOperator?.operator_description ?? ''}
                actions={detailOperator && (
                    <>
                        <button
                            onClick={() => {
                                setDetailOperator(null);
                                navigate(`/operator-launch/${detailOperator._id || detailOperator.id}`);
                            }}
                            className="w-full font-bold py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors border-none cursor-pointer"
                            style={{ backgroundColor: dsVar('accent'), color: dsVar('on-accent') }}
                        >
                            <RocketLaunchIcon fontSize="small" />
                            Add
                        </button>
                        <button
                            onClick={() => {
                                setDetailOperator(null);
                                navigate('/AIStudio/customize', {
                                    state: {
                                        flow_template_code: detailOperator.flow_template_code,
                                        operator_id: detailOperator._id || detailOperator.id,
                                        operator_name: detailOperator.operator_name,
                                        fromOperator: true,
                                        returnTo: { pathname: '/intelligence-hub' }
                                    }
                                });
                            }}
                            className="w-full bg-transparent border border-accent-fg text-accent-fg hover:bg-accent/10 font-bold py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors cursor-pointer"
                        >
                            <SettingsIcon fontSize="small" />
                            Customize
                        </button>
                    </>
                )}
            />
        </div>
    );
};

export default IntelligenceHub;
